import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";

export type DraftSaveStatus =
  | { type: "no_changes" }
  | { type: "saving" }
  | { type: "saved"; timestamp: string };

interface UseDraftSaveStatusOptions {
  /** Whether there are unpublished changes */
  hasUnpublishedChanges: boolean;
  /** Whether a save is currently in progress */
  isSaving: boolean;
  /** ISO timestamp of when the draft was last saved */
  lastSavedAt: string | null;
}

/** A save faster than this never shows as saving. */
const SAVING_STATUS_DELAY_MS = 500;

/**
 * Hook that computes the current save status for display. A save only reads
 * as saving once it has run for a while, so a fast autosave goes from one
 * saved timestamp straight to the next.
 */
export function useDraftSaveStatus({
  hasUnpublishedChanges,
  isSaving,
  lastSavedAt,
}: UseDraftSaveStatusOptions): DraftSaveStatus {
  const [isSavingSlowly, setIsSavingSlowly] = useState(false);
  useEffect(() => {
    if (!isSaving) {
      return;
    }
    const timeout = setTimeout(() => {
      setIsSavingSlowly(true);
    }, SAVING_STATUS_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      setIsSavingSlowly(false);
    };
  }, [isSaving]);

  return useMemo(() => {
    if (isSaving && isSavingSlowly) {
      return { type: "saving" };
    }

    if (hasUnpublishedChanges && lastSavedAt) {
      return { type: "saved", timestamp: lastSavedAt };
    }

    return { type: "no_changes" };
  }, [hasUnpublishedChanges, isSaving, isSavingSlowly, lastSavedAt]);
}

/**
 * Format a timestamp as a relative time string.
 * - "just now" for < 1 minute
 * - "X minutes ago" for < 60 minutes
 * - "X hours ago" for < 24 hours
 * - "X days ago" for <= 5 days
 * - "MMM d, yyyy" for > 5 days
 */
export function formatRelativeTime(timestamp: string): string {
  const dt = DateTime.fromISO(timestamp);
  const now = DateTime.now();
  const diff = now.diff(dt, ["days", "hours", "minutes"]);

  if (diff.minutes < 1) {
    return "just now";
  }

  if (diff.days > 5) {
    return dt.toFormat("MMM d, yyyy");
  }

  if (diff.days >= 1) {
    const days = Math.floor(diff.days);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }

  if (diff.hours >= 1) {
    const hours = Math.floor(diff.hours);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }

  const minutes = Math.floor(diff.minutes);
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
}
